/**
 * MainLayout component serves as the primary layout for the application.
 * It handles routing, socket initialization, notifications, and various UI components.
 * 
 * @class MainLayout
 * @extends {Component}
 * 
 * @property {Object} state - The component's state.
 * @property {boolean} state.trackOrderVisible - Determines if the track order popup is visible.
 * @property {string} state.direction - Tracks the scroll direction.
 * @property {number} state.lastScrollPos - Stores the last scroll position.
 * @property {Object|null} state.socket - The socket.io client instance.
 * @property {number} state.vh - The viewport height for handling lock scroll.
 * 
 * @property {Object} props - The component's props.
 * @property {boolean} props.loading - Indicates if the application is in a loading state.
 * @property {boolean} props.sideMenuOpen - Indicates if the side menu is open.
 * @property {boolean} props.lockScroll - Indicates if scrolling is locked.
 * @property {boolean} props.authenticated - Indicates if the user is authenticated.
 * @property {Array} props.paths - List of static paths for routing.
 * @property {Array} props.categories - List of item categories.
 * @property {string} props.status - The current status of the application (e.g., online/offline).
 * @property {Object} props.location - The current location object from react-router.
 * @property {Function} props.history - The history object from react-router for navigation.
 * @property {Function} props.clearOrder - Action to clear the order state.
 * @property {Function} props.showAuthenticationPopup - Action to show the authentication popup.
 * @property {Function} props.signinWithCookiePromise - Action to sign in using cookies.
 * @property {Function} props.queryStatus - Action to query the application's status.
 * @property {Function} props.swal - Action to show a SweetAlert popup.
 * @property {Function} props.close - Action to close a SweetAlert popup.
 * @property {Function} props.setMaintenance - Action to set the maintenance status.
 * @property {Function} props.postSocketError - Action to handle socket errors.
 * @property {Function} props.getAllCategories - Action to fetch all item categories.
 * @property {Function} props.getUserNotification - Action to fetch user notifications.
 * @property {Function} props.showRoyalPopup - Action to show the royal popup.
 * @property {Function} props.setEID - Action to set the EID from the URL.
 * @property {Function} props.getStaticPaths - Action to fetch static paths for routing.
 * @property {Function} props.getPageLayoutStrapi - Action to fetch page layout data from Strapi.
 * @property {Function} props.accessTokenVerify - Action to verify the access token.
 * 
 * @method componentDidUpdate - Lifecycle method to handle updates to props and state.
 * @method componentDidMount - Lifecycle method to handle initialization logic.
 * @method componentDidCatch - Lifecycle method to handle errors in the component tree.
 * @method accessTokenVerifyCall - Verifies the access token when the user changes.
 * @method setupBeforeUnloadListener - Sets up a listener for the beforeunload event.
 * @method doBeforeUnload - Handles cleanup before the window unloads.
 * @method initSocket - Initializes the socket.io client and sets up event listeners.
 * @method getAnnouncement - Fetches announcements from the Strapi API.
 * @method autoShowRoyalPopup - Automatically shows the royal popup based on conditions.
 * @method showTrackOrder - Toggles the visibility of the track order popup.
 * 
 * @returns {JSX.Element} The rendered MainLayout component.
 */
import 'sweetalert/dist/sweetalert.css';
import React, { Component } from 'react';
import { findIndex, get, isEqual, map } from 'lodash';
import moment from 'moment';
import { frontloadConnect } from 'react-frontload';
import Loadable from 'react-loadable';
import NotificationSystem from 'react-notification-system';
import { connect } from 'react-redux';
import ReduxSweetAlert, { swal, close } from 'react-redux-sweetalert';
import { Route, Switch, withRouter, Redirect } from 'react-router-dom'; //Redirect
import io from 'socket.io-client';
import { signinWithCookiePromise, accessTokenVerify, getRedirectFromProvider } from './actions/auth/index';
import { postSocketError } from './actions/chat/index';
import { showAuthenticationPopup, queryStatus, setMaintenance, showEventPopup, showRoyalPopup, setEID } from './actions/common/index';
import { getCouponFromUrl, getCoupons } from './actions/coupon/index'; //displayCouponPopup
import { getAllCategories } from './actions/item-category/index';
import { getStaticPaths, getPageLayoutStrapi } from './actions/layout/index'
import { getUserNotification } from './actions/notification/index'
import { clearOrder } from './actions/order/index';
import { getDeliveryRates } from './actions/delivery/index';
import { getJsonFromUrl } from './assets/util';
import { getStrapiAPI } from './assets/strapi';
import { auth } from './assets/firebase';
import AuthenticationPopup from './components/AuthenticationPopup';
import CookieConsent from './components/common/CookieConsent';
import ScrollToTop from './components/common/ScrollToTop';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ResponsiveMenu from './components/common/ResponsiveMenu';
import RoyalPopup from './components/common/RoyalPopup';
import EventPopup from './components/EventPopup';
import Loading from './components/Loading';
import { IP, STATUS } from './config';
import Home from './views/Home';
import ItemAddedToCartNotify from './views/ItemAddedToCartNotify';
import PageRender from './views/PageRender';
import Maintenance from './views/Maintenance';
import AppOnlyPage from './views/AppOnly';
const PrivateRoute = Loadable({
    loader: () => import(/* webpackChunkName: "PrivateRoute"*/
        './components/auth/PrivateRoute'),
    loading: () => null,
    modules: ['PrivateRoute']
});
const ChatAndShopOrder = Loadable({
    loader: () => import(/* webpackChunkName: "ChatAndShopOrder"*/
        './views/ChatAndShopOrder'),
    loading: () => null,
    modules: ['ChatAndShopOrder']
});
const ChatAndShopThankyou = Loadable({
    loader: () => import(/* webpackChunkName: "ChatAndShopThankyou"*/
        './views/ChatAndShopThankyou'),
    loading: () => null,
    modules: ['ChatAndShopThankyou']
});
const LiveChat = Loadable({
    loader: () => import(/* webpackChunkName: "LiveChat"*/
        './components/livechat/LiveChat'),
    loading: () => null,
    modules: ['LiveChat']
});
const Page404 = Loadable({
    loader: () => import(/* webpackChunkName: "404"*/
        './views/error/NotFound'),
    loading: () => null,
    modules: ['NotFound']
});
const About = Loadable({
    loader: () => import(/* webpackChunkName: "About"*/
        './views/About'),
    loading: () => null,
    modules: ['About']
});
const AccountLayout = Loadable({
    loader: () => import(/* webpackChunkName: "AccountLayout"*/
        './components/layout/Account'),
    loading: () => null,
    modules: ['Account']
});
const Checkin = Loadable({
    loader: () => import(/* webpackChunkName: "Checkin"*/
        './views/coin/Checkin'),
    loading: () => null,
    modules: ['Checkin']
})

const BlogGrid = Loadable({
    loader: () => import(/* webpackChunkName: "BlogGrid"*/
        './views/BlogGrid'),
    loading: () => null,
    modules: ['BlogGrid']
});
const BrandList = Loadable({
    loader: () => import(/* webpackChunkName: "BrandList"*/
        './views/BrandList'),
    loading: () => null,
    modules: ['BrandList']
});
const Checkout = Loadable({
    loader: () => import(/* webpackChunkName: "Checkout"*/
        './views/Checkout'),
    loading: () => null,
    modules: ['Checkout']
});
const CheckoutThankYou = Loadable({
    loader: () => import(/* webpackChunkName: "CheckoutThankYou"*/
        './views/CheckoutThankYou'),
    loading: () => null,
    modules: ['CheckoutThankYou']
});
const ContactUs = Loadable({
    loader: () => import(/* webpackChunkName: "ContactUs"*/
        './views/ContactUs'),
    loading: () => null,
    modules: ['ContactUs']
});
const Deal = Loadable({
    loader: () => import(/* webpackChunkName: "Deal"*/
        './views/Deal'),
    loading: () => null,
    modules: ['Deal']
});
const DealCreditCard = Loadable({
    loader: () => import(/* webpackChunkName: "DealCreditCard"*/
        './views/DealCreditCard'),
    loading: () => null,
    modules: ['DealCreditCard']
});
const Flashsale = Loadable({
    loader: () => import(/* webpackChunkName: "Flashsale"*/
        './views/Flashsale'),
    loading: () => null,
    modules: ['Flashsale']
});
const ForgotView = Loadable({
    loader: () => import(/* webpackChunkName: "ForgotView"*/
        './views/Forgot'),
    loading: () => null,
    modules: ['ForgotView']
});
const Howto = Loadable({
    loader: () => import(/* webpackChunkName: "Howto"*/
        './views/Howto'),
    loading: () => null,
    modules: ['Howto']
});
const KoreaMain = Loadable({
    loader: () => import(/* webpackChunkName: "KoreaMain"*/
        './views/KoreaMain'),
    loading: () => null,
    modules: ['KoreaMain']
});
const LoginView = Loadable({
    loader: () => import(/* webpackChunkName: "LoginView"*/
        './views/Login'),
    loading: () => null,
    modules: ['LoginView']
});
const OrderDetailView = Loadable({
    loader: () => import(/* webpackChunkName: "OrderDetailView"*/
        './components/account/order/OrderDetail'),
    loading: () => null,
    modules: ['OrderDetailView']
});
const SignupView = Loadable({
    loader: () => import(/* webpackChunkName: "SignupView"*/
        './views/Signup'),
    loading: () => null,
    modules: ['SignupView']
});
const PrivacyPolicy = Loadable({
    loader: () => import(/* webpackChunkName: "PrivacyPolicy" */
        './views/PrivacyPolicy'),
    loading: () => null,
    modules: ['PrivacyPolicy']
});
const CookiePolicy = Loadable({
    loader: () => import(/* webpackChunkName: "CookiePolicy" */
        './views/CookiePolicy'),
    loading: () => null,
    modules: ['CookiePolicy']
});
const ProductDetail = Loadable({
    loader: () => import(/* webpackChunkName: "ProductDetail" */
        './views/ProductDetail'),
    loading: () => null,
    modules: ['ProductDetail']
});
const ReviewGrid = Loadable({
    loader: () => import(/* webpackChunkName: "ReviewGrid"*/
        './views/ReviewGrid'),
    loading: () => null,
    modules: ['ReviewGrid']
});
const ReviewPage = Loadable({
    loader: () => import(/* webpackChunkName: "ReviewPage"*/
        './views/ReviewPage'),
    loading: () => null,
    modules: ['ReviewPage']
});
const Partner = Loadable({
    loader: () => import(/* webpackChunkName: "Partner"*/
        './views/Partner'),
    loading: () => null,
    modules: ['Partner']
})
const StoreGrid = Loadable({
    loader: () => import(/* webpackChunkName: "StoreGrid"*/
        './views/StoreGrid'),
    loading: () => null,
    modules: ['StoreGrid']
});
const StoreLocation = Loadable({
    loader: () => import(/* webpackChunkName: "StoreLocation"*/
        './views/StoreLocation'),
    loading: () => null,
    modules: ['StoreLocation']
});
const SearchResult = Loadable({
    loader: () => import(/* webpackChunkName: "SearchResult"*/
        './views/SearchResult'),
    loading: () => null,
    modules: ['SearchResult']
});
const TermOfService = Loadable({
    loader: () => import(/* webpackChunkName: "TermOfService" */
        './views/TermOfService'),
    loading: () => null,
    modules: ['TermOfService']
});
const ExchangeReturn = Loadable({
    loader: () => import(/* webpackChunkName: "ExchangeReturn" */
        './views/ExchangeReturn'),
    loading: () => null,
    modules: ['ExchangeReturn']
});
const TrackOrderPopup = Loadable({
    loader: () => import(/* webpackChunkName: "TrackOrderPopup"*/
        './components/TrackOrderPopup'),
    loading: () => null,
    modules: ['TrackOrderPopup']
});
const UploadReceipt = Loadable({
    loader: () => import(/* webpackChunkName: "UploadReceipt"*/
        './views/UploadReceipt'),
    loading: () => null,
    modules: ['UploadReceipt']
});
const UploadReceiptThankYou = Loadable({
    loader: () => import(/* webpackChunkName: "UploadReceiptThankYou"*/
        './views/UploadReceiptThankYou'),
    loading: () => null,
    modules: ['UploadReceiptThankYou']
});
const AdminBlog = Loadable({
    loader: () => import(/* webpackChunkName: "AdminBlog" */
        './views/blog/bloglist_view'),
    loading: () => null,
    modules: ['AdminBlog']
})
const AdminBlogForm = Loadable({
    loader: () => import(/* webpackChunkName: "AdminBlogForm" */
        './views/blog/blogform_view'),
    loading: () => null,
    modules: ['AdminBlogForm']
})
const BlogContent = Loadable({
    loader: () => import(/* webpackChunkName: "BlogContent" */
        './views/blog/BlogContent'),
    loading: () => null,
    modules: ['BlogContent']
});
const BlogEditor = Loadable({
    loader: () => import(/* webpackChunkName: "BlogEditor" */
        './views/blog/BlogEditor'),
    loading: () => null,
    modules: ['BlogEditor']
});
const AddReview = Loadable({
    loader: () => import(/* webpackChunkName: "AddReview"*/
        './views/rich_comment/AddReview'),
    loading: () => null,
    modules: ['AddReview']
});
const SelectShade = Loadable({
    loader: () => import(/* webpackChunkName: "SelectShade"*/
        './views/rich_comment/SelectShade'),
    loading: () => null,
    modules: ['SelectShade']
});
const GiveawayMain = Loadable({
    loader: () => import(/* webpackChunkName: "GiveawayMain"*/
        './views/giveaways/GiveawayMain'),
    loading: () => null,
    modules: ['GiveawayMain']
})

const GiveawayForm = Loadable({
    loader: () => import(/* webpackChunkName: "GiveawayForm"*/
        './views/giveaways/GiveawayForm'),
    loading: () => null,
    modules: ['GiveawayForm']
})

const GiveawayDetail = Loadable({
    loader: () => import(/* webpackChunkName: "GiveawayDetail"*/
        './views/giveaways/GiveawayDetail'),
    loading: () => null,
    modules: ['GiveawayDetail']
})

const TopSpender = Loadable({
    loader: () => import(/* webpackChunkName: "TopSpender"*/
        './views/TopSpender'),
    loading: () => null,
    modules: ['TopSpender']
})

const The1LuckyRegister = Loadable({
    loader: () => import(/* webpackChunkName: "The1LuckyRegister"*/
        './views/event/the1/The1LuckyRegister'),
    loading: () => null,
    modules: ['The1LuckyRegister']
})

const FirebaseAction = Loadable({
    loader: () => import(/* webpackChunkName: "FirebaseAction"*/
        './views/FirebaseAction'),
    loading: () => null,
    modules: ['FirebaseAction']
})

/* eslint-enable */

const frontLoad = async props => {
    try {
        // Execute all data fetching in parallel
        const [status, paths, signinResult] = await Promise.all([
            props.queryStatus(),
            props.getStaticPaths(),
            props.signinWithCookiePromise()
        ]);

        // If we have paths, check if we need to load page layout
        if (paths) {
            const i = findIndex(paths, (p) => p.link === props.location.pathname);
            if (i !== -1) {
                await props.getPageLayoutStrapi(paths[i].name);
            }
        }

        return signinResult;
    } catch (error) {
        console.error('Error in frontLoad:', error);
        throw error;
    }
};

var unsubscribe;
class MainLayout extends Component {

    constructor(props) {
        super(props);

        this.state = {
            trackOrderVisible: false,
            direction: '',
            lastScrollPos: 0,
            socket: null,
            vh: 0,
        }

        // this.trackPixelEvent = this.trackPixelEvent.bind(this)
        this.autoShowRoyalPopup = this.autoShowRoyalPopup.bind(this)
        this.showTrackOrder = this.showTrackOrder.bind(this)
        this.initSocket = this.initSocket.bind(this)
        this.getAnnouncement = this.getAnnouncement.bind(this);
        this.accessTokenVerifyCall = this.accessTokenVerifyCall.bind(this);
    }

    componentDidUpdate(prevProps) {
        // if (this.props.secret !== prevProps.secret && this.props.secret) {
        //     this.initSocket()
        // }

        if (this.props.notification !== prevProps.notification && prevProps.notification !== null) {
            this.refs.notificationSystem.addNotification(this.props.notification);
        }

        if (this.props.authenticated !== prevProps.authenticated && prevProps.authenticated === true) {
            if (this.socket) {
                this.socket.emit('user connect', this.props.secret, this.props.authenticated ? this.props.user_id : 1);
            }
        }

        if (!isEqual(this.props.location, prevProps.location)) {
            if (this.socket) {
                this.socket.emit('set last page', this.props.secret, this.props.user_id, this.props.location.pathname)
            }
        }

        if (this.props.couponUrl !== prevProps.couponUrl) {
            this.props.history.push({ pathname: this.props.location.pathname })
        }

        if (this.props.message !== prevProps.message && prevProps.status === STATUS.ONLINE) {
            if (this.props.message) {
                this.props.swal({ type: this.props.type, title: this.props.title, text: this.props.message, html: true, onConfirm: this.props.close, customClass: 'bt-dialog-box' });
            }
        }

        if (!prevProps.lockScroll && this.props.lockScroll) {
            let vh = window.innerHeight * 0.01 * 100;
            this.setState({ vh });
        }
        if (prevProps.profile !== this.props.profile) {
            if (this.props.profile && !unsubscribe) {
                unsubscribe = auth.onIdTokenChanged(this.accessTokenVerifyCall);
            } else {
                if (unsubscribe) {
                    unsubscribe();
                }
            }
        }
    }

    componentDidMount() {
        // this.props.registerDevice();
        this.autoShowRoyalPopup()
        this.getAnnouncement();
        this.initSocket();
        this.props.getAllCategories();
        this.props.getDeliveryRates();
        const searchQuery = getJsonFromUrl(this.props.location.search);
        if (searchQuery.eid) {
            this.props.setEID(searchQuery.eid);
        }
    }

    componentDidCatch(error, info) {
        console.log(error, info);
        // this.props.history.push('/404');
    }

    async accessTokenVerifyCall(user) {
        if (user) {
            const token = await user.getIdToken();
            this.props.accessTokenVerify(token);
        }
    }

    setupBeforeUnloadListener() {
        window.addEventListener("beforeunload", this.doBeforeUnload);
    };

    doBeforeUnload(e) {
        e.preventDefault()
        if (this.socket) {
            this.socket.emit('disconnect user', this.props.secret)
            this.socket.disconnect()
        }
    }

    initSocket() {
        this.socket = io(IP, { transports: ['polling'] });
        this.socket.on('connect', () => {
            this.socket.emit('user connect', this.props.secret, this.props.authenticated ? this.props.user_id : 1);
        })
        this.socket.on('maintenance', (v) => {
            this.props.setMaintenance(v);
            if (v === STATUS.ONLINE) {
                window.location.reload()
            }
        })
        this.socket.on('error', (error) => {
            this.props.postSocketError(error)
        });

        this.socket.on('notification', (error) => {
            this.props.getUserNotification();
        })

        this.socket.connect()
        this.setState({ socket: this.socket });
    }

    getAnnouncement() {
        getStrapiAPI(`/header-announcements`)
            .then((result) => {
                this.setState({ announcement: get(result, "announcement", null) })
            })
    }

    autoShowRoyalPopup() {
        const now = moment();
        getStrapiAPI(`/popups`)
            .then((result) => {
                let userForceCloseDate = localStorage.getItem('RoyalPopupForceCloseDate') ? moment(localStorage.getItem('RoyalPopupForceCloseDate')) : moment('1970-01-01')
                const show = { img: get(result, "image.url", null), name: get(result, "image.alternativeText", ""), link: get(result, "link", null), close_button_color: get(result, "close_button_color", "#ccc") }
                if (now.diff(userForceCloseDate, "m") >= 15) {
                    this.props.showRoyalPopup(show)
                }
            })
        // const eventStartDate = moment("2021-08-27"), eventEndDate = moment("2021-09-26")
        // function getPopup() {
        //     if (now.isSameOrBefore("2021-09-23", "d")) {
        //         return "https://cdn.thebeautrium.com/campaign/2021-08-halfprice/popup/POP_UP-01.png";
        //     } else if (now.isSame("2021-09-24", "d")) {
        //         return "https://cdn.thebeautrium.com/campaign/2021-08-halfprice/popup/POP_UP-02.png";
        //     } else if (now.isSame("2021-09-25", "d")) {
        //         return "https://cdn.thebeautrium.com/campaign/2021-08-halfprice/popup/POP_UP-03.png";
        //     } else if (now.isSame("2021-09-26", "d")) {
        //         return "https://cdn.thebeautrium.com/campaign/2021-08-halfprice/popup/POP_UP-04.png";
        //     }
        // }
        // if (now.isBetween(eventStartDate, eventEndDate, 'd', "[]")) {
        //     let show = {img: getPopup(), link: null}
        //     let userForceCloseDate = localStorage.getItem('RoyalPopupForceCloseDate')? moment(localStorage.getItem('RoyalPopupForceCloseDate')): moment('1970-01-01')
        //     if (now.diff(userForceCloseDate, "m") >= 15) {
        //         this.props.showRoyalPopup(show)
        //     }
        //     // show = (userForceCloseDate.isSameOrBefore(eventEndDate) && userForceCloseDate.add(1, 'd').isBefore(moment())) || userForceCloseDate.isBefore(eventStartDate)
        // }

    }

    showTrackOrder(v = true) {
        this.props.clearOrder();
        this.setState((prev) => { return { trackOrderVisible: v } });
    }

    render() {
        const { loading, sideMenuOpen, lockScroll, authenticated, paths, categories } = this.props; //initSignin
        const { announcement } = this.state;
        const notificationStyle = {
            Containers: {
                tr: {
                    top: '62px'
                }
            },
            NotificationItem: {
                info: {
                    color: '#fff', //'#f40e33',
                    backgroundColor: '#000000',
                    borderTop: '2px solid #cccccc'
                }
            },
            Dismiss: {
                info: {
                    color: '#fff',
                    backgroundColor: '#000'
                }
            }
        };
        const ALWAYS_ONLINE_PAGES = ['/privacy', '/tos'];
        if (this.props.status === STATUS.OFFLINE && !ALWAYS_ONLINE_PAGES.includes(this.props.location.pathname)) {
            return <Maintenance />
        }
        const st = { maxWidth: "100vw" }
        if (lockScroll) {
            st.height = this.state.vh;
        }
        // if (!categories || categories.length === 0) {
        //     return <Loading />;
        // }
        return (
            <ScrollToTop>
                {loading && <Loading />}
                <div
                    id="main"
                    className={`main ${(sideMenuOpen || lockScroll) ? "side-menu-open" : ""}`}
                    style={st}
                    ref="main">
                    <Header showTrackOrder={this.showTrackOrder} authenticated={this.props.authenticated} announcement={announcement} />
                    <div id="bt-content" className={`bt-content ${Boolean(announcement) ? "with-announcement" : ""}`}>
                        {/* <Progress /> */}
                        <Switch>
                            <Route exact path={`/`} component={Home} />
                            {/* Campaign */}
                            {/* <Route exact path={`/supersale`} component={SuperSaleMain}/> */}
                            {/* Campaign End */}
                            <Route path={`/aboutus`} component={About} />
                            <Route path={`/contactus`} component={ContactUs} />
                            <Route path={`/partner`} component={Partner} />
                            <Route path={`/exchange_return`} component={ExchangeReturn} />
                            <Route path={`/store_locations/:store_id`} component={StoreLocation} />
                            <Route path={`/store_locations`} component={StoreLocation} />
                            <Route path={`/reviews`} component={ReviewGrid} />
                            <Route path={`/review/:id`} component={ReviewPage} />
                            {/* <Route path={`/best-sellers`} component={BestSeller} /> */}
                            <Route path={`/store/:param1?/:param2?/:param3?/:param4?`} render={(props) => <Redirect to={{ pathname: props.match.url.replace("/store", "/shop"), search: (props.location.search ? props.location.search : "") }} />} />
                            <Route path={`/shop/:param1?/:param2?/:param3?/:param4?`} component={StoreGrid} />
                            <Route path={`/brands`} component={BrandList} />
                            <Route exact path={`/deals`} component={Deal} /> {/* (props) => <Deal {...props} /> */}
                            <Route exact path={`/deals/creditcard`} component={DealCreditCard} />
                            <Route exact path={`/deals/:type`} component={Deal} />
                            <Route path={`/flashsale/:id?`} component={Flashsale} />
                            <Route exact path={`/item/:mat_id/:mat_var_id/addreview`} component={AddReview} />
                            <Route path={`/item/:mat_id/:mat_var_id/selectshade`} component={SelectShade} />
                            <Route path={`/item/:mat_id/:mat_var_id`} component={ProductDetail} />
                            <Route path={`/item/:mat_id`} component={ProductDetail} />
                            <Route exact path={`/checkout/thankyou/:so_no`} component={CheckoutThankYou} />
                            <Route exact path={`/checkout/thankyou`} component={CheckoutThankYou} />
                            <Route exact path={`/checkout`} component={Checkout} />
                            <Route path={`/login`} component={LoginView} />
                            <Route path={`/signup`} component={SignupView} /> {/* <Route exact path={`/order/detail/:id/:payment`} component={OrderDetailView} /> */}
                            <Route exact path={`/order/:id`} component={OrderDetailView} />
                            <Route path={`/account/coins/checkin`} component={Checkin} />
                            <Route path={`/account`} component={AccountLayout} />
                            {/* <PrivateRoute authed={this.props.authenticated} showAuthenticationPopup={this.props.showAuthenticationPopup} path={`/account`} component={AccountLayout} initSignin={initSignin} /> */}
                            <Route path={`/forgot_password`} component={ForgotView} />
                            <Route exact path={`/upload_receipt`} component={UploadReceipt} />
                            <Route exact path={`/upload_receipt/thank_you`} component={UploadReceiptThankYou} />
                            {/* <Route path={`/blogger/:id`} component={BloggerProfile}/> */}
                            <Route path={`/search/:keyword`} component={SearchResult} />
                            <Route path={`/auth/action`} component={FirebaseAction} />
                            <Route path={`/blogs`} component={BlogGrid} />
                            <Route path={`/adminblog`} component={AdminBlog} />
                            <PrivateRoute
                                authed={this.props.authenticated}
                                path={`/adminblogform/:id`}
                                component={AdminBlogForm}
                                allowedRole={[0, 4, 8]} />
                            <PrivateRoute
                                authed={this.props.authenticated}
                                exact
                                path={`/blog/preview/:id`}
                                component={BlogContent}
                                showAuthenticationPopup={this.props.showAuthenticationPopup} />
                            <Route path={`/blog/:id`} component={BlogContent} />
                            <Route
                                authed={this.props.authenticated}
                                component={BlogEditor}
                                allowedRole={[0, 4, 8]}
                                path={`/editor/:id?`}
                            /> {/* temp */}
                            <Route path={`/howto`} component={Howto} />
                            <Route path={`/privacy`} component={PrivacyPolicy} />
                            <Route path={`/cookie-policy`} component={CookiePolicy} />
                            <Route path={`/tos`} component={TermOfService} />
                            {/* <Route path={`/testpro`} component={TestPro}/> */}
                            {/* <Route path={`/order/payment/:token`} component={CheckoutPayment}/> */}
                            <Route path={`/order/token/:token`} component={OrderDetailView} />
                            <Route path={`/korea`} component={KoreaMain} />

                            <Route path={`/chat_order`} exact component={ChatAndShopOrder} />
                            <Route path={`/chat_order/thankyou`} component={ChatAndShopThankyou} />
                            <Route path={`/event/:event_code`} component={TopSpender} />
                            {/*Giveaway*/}
                            <Route path={`/campaign/giveaway/create`} component={GiveawayForm} />
                            <Route path={`/campaign/giveaway/:id`} component={GiveawayDetail} />
                            <Route path={`/campaign/giveaway`} component={GiveawayMain} />
                            {/*End Region Giveaway*/}
                            <Route path={`/the1`} component={The1LuckyRegister} />
                            {map(paths, (path, n) => {
                                if (path.link.startsWith('/special-event')) {
                                    return <Route key={`page_${path.name}_${n}`} path={path.link} component={AppOnlyPage} />
                                } else if (path.link !== "/") {
                                    const obj = {
                                        title: get(path, "seo_title", null),
                                        title_suffix: false,
                                        description: get(path, "seo_description", null),
                                        keywords: get(path, "seo_keywords", null),
                                        og_image: get(path, "og_image.url", null),
                                        imageType: get(path, "og_image.mime", null)
                                    }
                                    return <Route key={`page_${path.name}_${n}`} path={path.link} render={(props) => <PageRender page={path.name} {...props} {...obj} />} />
                                }
                                return null;
                            })}
                            <Route path={`*`} component={Page404} />
                        </Switch>
                        <Footer showTrackOrder={this.showTrackOrder} />
                    </div>
                    <ResponsiveMenu showTrackOrder={this.showTrackOrder} onAccount={this.props.location.pathname.startsWith('/account')} />
                    <CookieConsent />
                    <ReduxSweetAlert />
                    <NotificationSystem ref="notificationSystem" style={notificationStyle} />
                    <ItemAddedToCartNotify closePopup={() => this.setState({ addToCartVisible: false })} />
                    {this.state.socket && <LiveChat socket={this.state.socket} />}
                    {this.state.trackOrderVisible && <TrackOrderPopup closeTrackOrder={() => this.setState({ trackOrderVisible: false })} />}
                    <AuthenticationPopup />
                    {/* {< CollectCouponPopup />} */}
                    <EventPopup authenticated={authenticated} />
                    <RoyalPopup />
                </div>
            </ScrollToTop>
        );
    }
}

// function PrivateRoute ({component: Component, authed,
// showAuthenticationPopup, initSignin, ...rest}) {     if (!authed) {
// showAuthenticationPopup(true);     }     return <Route {...rest}
//        render={(props) => authed === true                     ? <Component
// {...props} />                     : <Redirect to={{pathname: '/', state:
// {from: props.location}}} />}             /> }

function mapStateToProps(state) {
    const profile = state.prof.profile;
    return {
        loading: state.common.loading,
        authenticated: state.auth.authenticated,
        user_id: state.auth.user_id,
        email: profile ? profile.email : '',
        notification: state.common.notification,
        sideMenuOpen: state.common.show_responsive_menu,
        lockScroll: state.common.lock_scroll,
        initSignin: state.auth.initSignin,

        title: state.common.title,
        status: state.common.status,
        message: state.common.text,
        type: state.common.type,

        secret: state.auth.secret,
        couponUrl: state.coupon.couponUrl,

        paths: state.layout.paths,

        categories: state.itemCategories.categories,
    }
}

export default withRouter(connect(mapStateToProps, {
    clearOrder,
    showAuthenticationPopup,
    signinWithCookiePromise,
    queryStatus,
    swal,
    close,
    setMaintenance,
    postSocketError,
    // registerDevice,
    getCouponFromUrl,
    showEventPopup,
    getCoupons,
    // displayCouponPopup,
    getAllCategories,
    getUserNotification,
    showRoyalPopup,
    setEID,
    getStaticPaths,
    getPageLayoutStrapi,
    accessTokenVerify,
    getRedirectFromProvider,
    getDeliveryRates
})(frontloadConnect(frontLoad, {
    onMount: true,
    onUpdate: false
})(MainLayout)));
